import useResizeObserver from "@react-hook/resize-observer";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from "react";
import { toast } from "react-toastify";

import { Table, TableColumnType } from "antd";

import { useUserEvents } from "hooks";

import { EntityKind } from "models/entityKind";

import useGetFacilityNetworkFraction from "components/map/hooks/useGetFacilityNetworkFraction";
import getDaysInMonth from "components/vis/util/getDaysInMonth";
import { useWellList } from "components/well-list/context";

import useFetchWellDownstreamReport from "../../hooks/useFetchWellDownstreamReport";
import {
  midstreamNavigatorActions,
  useMidstreamNavigatorContext
} from "../context/MidstreamNavigatorContext";
import { CustomFacilityHeaderRow } from "./CustomFacilityHeaderRow";
import {
  EmptyTablePlaceholder,
  OverlayContainer,
  RootContainer,
  TableContainer
} from "./MidstreamNavigatorTable.styles";
import { getFacilityColumns } from "./columns/facilityColumns";
import { getWellColumns } from "./columns/wellColumns";
import type {
  WellContribution,
  WellDownstreamApiItem,
  WellDownstreamData
} from "./types";
import {
  calculateFacilityTotals,
  formatNumberWithThousandSeparator
} from "./utils/numberUtils";

interface MidstreamNavigatorTableProps {
  showOverlay: boolean;
  /**
   * True while the entity list cannot be trusted to answer for the current mode:
   * its wells or its grouped entities are loading, or a filter is being applied.
   */
  isWellListLoading?: boolean;
}

export interface MidstreamNavigatorTableRef {
  copyTableToClipboard: () => Promise<void>;
}

const MidstreamNavigatorTable = forwardRef<
  MidstreamNavigatorTableRef,
  MidstreamNavigatorTableProps
>(({ showOverlay, isWellListLoading = false }, ref) => {
  const [midstreamState, dispatch] = useMidstreamNavigatorContext();
  const { sendCopyDataTableEvent } = useUserEvents();
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerHeight, setContainerHeight] = useState(400);
  const [showEntityNotInList, setShowEntityNotInList] = useState(false);

  // Well list context
  const [wellListState] = useWellList();
  const selectedWell = wellListState?.selectedWell;
  const wells = wellListState?.wells;

  const facilityId = selectedWell?.Uwi || "";
  const { data: facilityData, isFetching: isFetchingFacilityData } =
    useGetFacilityNetworkFraction(facilityId);

  const wellIds = useMemo(
    () => (selectedWell?.Uwi ? [selectedWell.Uwi] : []),
    [selectedWell]
  );
  const { data: wellData, isFetching: isFetchingWellData } = useFetchWellDownstreamReport(
    {
      month: midstreamState.selectedMonth,
      ids: wellIds
    }
  );

  useResizeObserver(containerRef, (client) => {
    if (client) {
      setContainerHeight(client.contentRect.height);
    }
  });

  // Sync the month from API response to context state (similar to WellDownstreamReport pattern)
  useEffect(() => {
    if (wellData?.month && wellData.month !== midstreamState.selectedMonth) {
      dispatch(midstreamNavigatorActions.setSelectedMonth(wellData.month));
    }
  }, [wellData?.month, midstreamState.selectedMonth, dispatch]);

  // Calculate scroll height based on mode - facility mode needs more space for custom header
  const getScrollHeight = (containerHeight: number): number => {
    if (midstreamState.mode === EntityKind.Facility) {
      return Math.max(containerHeight - 135, 150);
    } else {
      return Math.max(containerHeight - 100, 200);
    }
  };

  // Process data based on mode with performance optimizations
  const { dataSource, columns } = useMemo(() => {
    if (midstreamState.mode === EntityKind.Facility) {
      // Facility mode - show well contributions
      if (!facilityData || !selectedWell?.Uwi) {
        return { dataSource: [], columns: getFacilityColumns() };
      }

      // Pre-calculate values to avoid repeated calculations in map
      const minContribution = midstreamState.minContributionPercent ?? 0;

      const wellContributions: WellContribution[] = facilityData
        .map((item, index) => {
          // Pre-calculate contribution percentage for filtering
          const contributionPercent = Math.round(item.gasPct * 10) / 10;

          if (
            midstreamState.minContributionPercent !== null &&
            contributionPercent < minContribution
          ) {
            return null;
          }

          const daysInMonth = getDaysInMonth(item.productionMonth);

          return {
            key: index,
            uwi: item.wellName,
            resourcePlay: item.resourcePlay,
            operator: item.operator,
            gasRate: daysInMonth
              ? formatNumberWithThousandSeparator(item.gasMcf / daysInMonth, 2)
              : "",
            monthlyGasVolume: formatNumberWithThousandSeparator(item.gasMcf * 0.001, 2),
            value: contributionPercent.toFixed(1),
            distance: item.nodeDistance
          };
        })
        .filter(Boolean) as WellContribution[];

      return {
        dataSource: wellContributions,
        columns: getFacilityColumns() as TableColumnType<
          WellContribution | WellDownstreamData
        >[]
      };
    } else {
      // Well mode - show downstream report
      if (!wellData?.network) {
        return {
          dataSource: [],
          columns: getWellColumns() as TableColumnType<
            WellContribution | WellDownstreamData
          >[]
        };
      }

      // Cast the network data to the correct type since the hook type is incorrect
      const networkData = wellData.network as unknown as WellDownstreamApiItem[];

      // Pre-calculate min contribution for performance
      const minContribution = midstreamState.minContributionPercent ?? 0;

      const downstreamData: WellDownstreamData[] = networkData
        .map((item, index) => {
          // Convert fraction to percentage for filtering
          const contributionPercent = item.fraction * 100;

          if (
            midstreamState.minContributionPercent !== null &&
            contributionPercent < minContribution
          ) {
            return null;
          }

          return {
            key: index,
            wellId: item.wellId,
            destinationFacility: item.destinationFacility || "",
            facilityOperator: item.facilityOperator || "",
            depth: item.depth,
            fraction: item.fraction
          };
        })
        .filter(Boolean) as WellDownstreamData[];

      return {
        dataSource: downstreamData,
        columns: getWellColumns() as TableColumnType<
          WellContribution | WellDownstreamData
        >[]
      };
    }
  }, [
    midstreamState.mode,
    midstreamState.minContributionPercent,
    facilityData,
    selectedWell,
    wellData
  ]);

  const selectedEntityNotInList = useMemo(() => {
    // Until the list for the mode just switched to has arrived, `wells` still
    // holds the other mode's entities; judging the selection against it would
    // claim an on-screen entity is off the map.
    if (isWellListLoading) return false;
    if (!wells || wells.length === 0) return false;
    if (!selectedWell?.Uwi) return false;

    if (midstreamState.mode === EntityKind.Well) {
      // For well mode: only show if not fetching and we have data
      if (isFetchingWellData) return false;
      if (!wellData?.network || wellData.network.length === 0) return false;
      return !wells.some((well) => well.id === selectedWell.Uwi);
    } else {
      // For facility mode: only show if not fetching and we have data
      if (isFetchingFacilityData) return false;
      if (!facilityData || facilityData.length === 0) return false;
      return !wells.some((item) => item.id === selectedWell.Uwi);
    }
  }, [
    selectedWell,
    wells,
    midstreamState.mode,
    isWellListLoading,
    isFetchingWellData,
    isFetchingFacilityData,
    wellData,
    facilityData
  ]);

  // Debounce the display of the "not in list" message to prevent flash during mode transitions
  useEffect(() => {
    if (selectedEntityNotInList) {
      const timer = setTimeout(() => {
        setShowEntityNotInList(true);
      }, 300); // 300ms delay to allow mode transition to complete

      return () => clearTimeout(timer);
    } else {
      setShowEntityNotInList(false);
    }
  }, [selectedEntityNotInList]);

  // Expose copy function to parent component via ref
  useImperativeHandle(
    ref,
    () => ({
      copyTableToClipboard
    }),
    [dataSource, columns, midstreamState.mode]
  );

  const copyTableToClipboard = async () => {
    if (!navigator.clipboard) {
      toast.error("Clipboard copy is not supported by the browser.");
      return;
    }

    try {
      const headerRow = columns.map((col) => col.title).join("\t");
      let csvContent = headerRow;

      if (midstreamState.mode === EntityKind.Facility && dataSource.length > 0) {
        const wellContributions = dataSource as WellContribution[];
        const { sumGasRate, sumMonthlyGas } = calculateFacilityTotals(wellContributions);

        const totalsRow = [
          "Facility Total", // UWID column
          "", // Resource Play column (empty)
          "", // Operator column (empty)
          sumGasRate, // Gas Rate column
          sumMonthlyGas, // Monthly Gas column
          "", // Contribution Percent column (empty)
          "" // Nodal Distance column (empty)
        ].join("\t");

        csvContent += "\n" + totalsRow;
      }

      const dataRows = dataSource
        .map((row: WellContribution | WellDownstreamData) =>
          columns
            .map((col) => {
              const value = row[col.dataIndex as string];

              // Handle special formatting for well mode columns
              if (
                midstreamState.mode === EntityKind.Well &&
                col.dataIndex === "fraction"
              ) {
                // Convert fraction to percentage with 1 decimal place (same as render function)
                return value !== null && value !== undefined
                  ? (value * 100).toFixed(1)
                  : "";
              }

              return value !== null && value !== undefined ? value : "";
            })
            .join("\t")
        )
        .join("\n");

      if (dataRows) {
        csvContent += "\n" + dataRows;
      }
      sendCopyDataTableEvent({
        source: "midstream-navigator",
        midstreamMode: midstreamState.mode,
        selectedEntityId: selectedWell?.Uwi || "",
        monthSelected:
          midstreamState.mode === EntityKind.Well
            ? midstreamState.selectedMonth
            : undefined,
        minContributionPercent:
          midstreamState.mode === EntityKind.Well
            ? midstreamState.minContributionPercent
            : undefined,
        rowCount: dataRows.length,
        columnCount: columns.length
      });

      await navigator.clipboard.writeText(csvContent);
    } catch (err) {
      toast.error("Failed to copy to clipboard.");
    }
  };

  const getEmptyMessage = () => {
    if (midstreamState.mode === EntityKind.Facility) {
      return "Please select a facility to get started.";
    } else {
      return "Please select a well to get started.";
    }
  };

  return (
    <RootContainer>
      <TableContainer ref={containerRef}>
        {showOverlay ? null : (midstreamState.mode === EntityKind.Facility &&
            !selectedWell?.Uwi) ||
          (midstreamState.mode === EntityKind.Well && wellIds.length === 0) ? (
          <EmptyTablePlaceholder>
            <OverlayContainer>{getEmptyMessage()}</OverlayContainer>
          </EmptyTablePlaceholder>
        ) : showEntityNotInList ? (
          <EmptyTablePlaceholder>
            <OverlayContainer>
              Selected {midstreamState.mode === EntityKind.Well ? "Well" : "Facility"} is
              not within map view
            </OverlayContainer>
          </EmptyTablePlaceholder>
        ) : (
          <Table
            className={
              midstreamState.mode === EntityKind.Facility ? "facilityModeTable" : ""
            }
            size="small"
            dataSource={dataSource}
            columns={columns}
            pagination={{
              pageSize: 100,
              showSizeChanger: false,
              showQuickJumper: false,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
              size: "small"
            }}
            sticky
            scroll={{
              y: getScrollHeight(containerHeight),
              scrollToFirstRowOnChange: true,
              x: "max-content"
            }}
            components={
              midstreamState.mode === EntityKind.Facility
                ? {
                    header: {
                      row: (props: React.HTMLAttributes<HTMLTableRowElement>) => (
                        <CustomFacilityHeaderRow
                          {...props}
                          columns={columns}
                          wellContributions={dataSource as WellContribution[]}
                        />
                      )
                    }
                  }
                : undefined
            }
          />
        )}
      </TableContainer>
    </RootContainer>
  );
});

MidstreamNavigatorTable.displayName = "MidstreamNavigatorTable";

export default MidstreamNavigatorTable;
